import {useSearchArtistsQuery} from '@graphql/generated';
import type {ArtistFragment} from '@graphql/generated';

import {Avatar, Box, Center, Flex, Heading, LoadingSpinner} from '@tempo/core';
import {useTranslation} from 'next-i18next';

interface ArtistResultProps {
	query: string;
	addArtist: (airtstId: string, artist: ArtistFragment) => void;
}

const ArtistResult = ({query, addArtist}: ArtistResultProps) => {
	const {t} = useTranslation();

	const {data, loading} = useSearchArtistsQuery({
		variables: {
			query,
		},
		skip: !query,
	});

	const artists = data?.search?.results?.artists?.edges
		?.filter(({node}) => node)
		.map(({node}) => node);

	if (loading) {
		return (
			<Center h="100%">
				<LoadingSpinner />
			</Center>
		);
	}

	if (query === '') {
		return null;
	}

	if (!loading && artists?.length === 0) {
		return (
			<Center h="100%">
				{t('searchemptystate_text_noresultsforsearcheditemX_web', {
					sprintf: [query],
				})}
			</Center>
		);
	}

	return (
		<Box pt="spacing.l" px="spacing.l">
			{artists?.map((artist) =>
				artist ? (
					<Flex
						w="100%"
						key={artist.id}
						as="button"
						onClick={() => addArtist(artist.id, artist)}
						align="center"
						position="relative"
						flex={1}
						padding="spacing.s"
						_hover={{
							backgroundColor: 'background.neutral.tertiary.hovered',
						}}
					>
						<Avatar
							name={artist.name}
							size="size.2xl"
							src={artist.picture?.medium[0]}
						/>

						<Box ms="spacing.l" textAlign="left">
							<Heading
								as="h3"
								noOfLines={1}
								variant="heading.xs"
								color="text.neutral.primary.default"
							>
								{artist.name}
							</Heading>
						</Box>
					</Flex>
				) : null,
			)}
		</Box>
	);
};

export default ArtistResult;
